import { ASSIGNMENTS_API_URL } from 'modules/assignments';
import { handleApiError } from 'utils/api';
import { logError } from 'utils/datadog';
import { env } from '../../constants';
import { AssignmentDetailsResponse } from './types';

const handleFetchError = (error: unknown, assignmentId: string): never => {
  if (error instanceof Error) {
    if (error.name === 'AbortError') {
      logError('Assignment details fetch timed out', {
        assignmentId,
        url: `${env.VITE_API_BASE_URL}/${ASSIGNMENTS_API_URL}/${assignmentId}`
      });
      throw new Error(
        'Request timed out. Please check your connection and try again.'
      );
    }

    if (error.message === 'Failed to fetch') {
      logError('Network error fetching assignment details', {
        assignmentId,
        url: `${env.VITE_API_BASE_URL}/${ASSIGNMENTS_API_URL}/${assignmentId}`,
        isOnline: navigator.onLine
      });
      throw new Error(
        'Network error. Please check your connection and try again.'
      );
    }
  }

  throw error;
};

const makeFetchRequest = async (
  token: string,
  assignmentId: string,
  signal: AbortSignal
): Promise<AssignmentDetailsResponse> => {
  const response = await fetch(
    `${env.VITE_API_BASE_URL}/${ASSIGNMENTS_API_URL}/${assignmentId}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`
      },
      signal
    }
  );

  if (!response.ok) {
    if ([401, 503].includes(response.status)) {
      window.location.reload();
    }
    await handleApiError(response);
  }

  return response.json() as Promise<AssignmentDetailsResponse>;
};

export const fetchAssignmentDetails = async (
  token: string | undefined,
  assignmentId: string | undefined
): Promise<AssignmentDetailsResponse | undefined> => {
  if (!token || !assignmentId) {
    return undefined;
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

  try {
    const result = await makeFetchRequest(
      token,
      assignmentId,
      controller.signal
    );
    return result;
  } catch (error) {
    handleFetchError(error, assignmentId);
  } finally {
    clearTimeout(timeoutId);
  }
};
